import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, Download, Trash2, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";

import {
  borrarIntercambioCarga,
  cargarIntercambios,
  descargarIntercambiosXlsx,
  getIntercambioCargas,
  getIntercambioDia,
  getIntercambios,
  type IntercambioCargaResultado,
} from "@/lib/api";

/*
 * Importaciones y exportaciones de energía: la "energía asignada" de los
 * enlaces internacionales (Eagle Pass, Laredo VFT, Railroad DC Tie, Guatemala,
 * Belice) que CENACE publica por participante y por día.
 *
 * El usuario arrastra los Excel "Energía asignada DDMMYYYY <participante>" —
 * varios de un jalón — y cada (día, participante) reemplaza su carga anterior.
 *
 * Convención de signo en las gráficas: la IMPORTACIÓN va hacia arriba (entra
 * energía al SIN) y la EXPORTACIÓN hacia abajo. El "neto" de los KPIs es
 * importado − exportado: positivo = importador neto.
 */

const nf0 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 1 });

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
function fmtFecha(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")} ${MESES[m - 1]} ${y}`;
}

// Color fijo por punto de interconexión (los tres enlaces con Texas en azules,
// Guatemala y Belice en cálidos) para que todas las vistas se lean igual.
const COLOR_PUNTO: Record<string, string> = {
  EAP: "#2563eb", LAA: "#0ea5e9", RRD: "#6366f1",
  GUA: "#f59e0b", BEL: "#14b8a6",
};
const COLOR_EXTRA = ["#a855f7", "#ec4899", "#84cc16", "#f97316", "#64748b"];
function colorDe(punto: string, i: number): string {
  return COLOR_PUNTO[punto] ?? COLOR_EXTRA[i % COLOR_EXTRA.length];
}

export function IntercambiosPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [partSel, setPartSel] = useState<string[]>([]);   // vacío = todos
  const [puntosSel, setPuntosSel] = useState<string[]>([]); // vacío = todos
  const [diaSel, setDiaSel] = useState<{ fecha: string; participante: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultados, setResultados] = useState<IntercambioCargaResultado[] | null>(null);

  const q = useQuery({
    queryKey: ["intercambios", desde, hasta, partSel, puntosSel],
    queryFn: () => getIntercambios(desde || null, hasta || null, partSel, puntosSel),
  });
  const cargasQ = useQuery({ queryKey: ["intercambios-cargas"], queryFn: getIntercambioCargas });
  const diaQ = useQuery({
    queryKey: ["intercambios-dia", diaSel?.fecha, diaSel?.participante],
    queryFn: () => getIntercambioDia(diaSel!.fecha, diaSel!.participante),
    enabled: diaSel != null,
  });

  const data = q.data;
  const cargas = cargasQ.data?.items ?? [];

  // El backend decide el rango por defecto (el mes de la última carga); en
  // cuanto llega, se refleja en los inputs para que el filtro no mienta.
  useEffect(() => {
    if (!data?.rango.desde || !data.rango.hasta) return;
    setDesde((v) => v || data.rango.desde!);
    setHasta((v) => v || data.rango.hasta!);
  }, [data?.rango.desde, data?.rango.hasta]);

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["intercambios"] });
    qc.invalidateQueries({ queryKey: ["intercambios-cargas"] });
    qc.invalidateQueries({ queryKey: ["intercambios-dia"] });
  };

  const subir = useMutation({
    mutationFn: (fd: FormData) => cargarIntercambios(fd),
    onSuccess: (r) => {
      setError(null);
      setResultados(r.resultados);
      invalidar();
    },
    onError: (e) => {
      setResultados(null);
      setError(e instanceof Error ? e.message : "No se pudieron cargar los archivos");
    },
  });

  const borrar = useMutation({
    mutationFn: (id: number) => borrarIntercambioCarga(id),
    onSuccess: () => { setDiaSel(null); invalidar(); },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar"),
  });

  const onArchivos = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    for (const f of Array.from(files)) fd.append("archivos", f);
    subir.mutate(fd);
    if (fileRef.current) fileRef.current.value = "";
  };

  const puntos = data?.puntos ?? [];
  // El orden de las series es el del catálogo, así el color y la pila no
  // bailan cuando un día le falta un punto.
  const puntosVisibles = useMemo(
    () => (puntosSel.length ? puntos.filter((p) => puntosSel.includes(p.punto)) : puntos),
    [puntos, puntosSel],
  );

  // Pivote día × punto para la gráfica apilada: importación positiva, exportación negativa.
  const serieDias = useMemo(() => {
    const porFecha = new Map<string, Record<string, number | string>>();
    for (const d of data?.dias ?? []) {
      let fila = porFecha.get(d.fecha);
      if (!fila) {
        fila = { fecha: d.fecha };
        porFecha.set(d.fecha, fila);
      }
      fila[`imp_${d.punto}`] = d.importado;
      fila[`exp_${d.punto}`] = -d.exportado;
    }
    return [...porFecha.values()].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
  }, [data?.dias]);

  const serieHoras = useMemo(
    () => (data?.horas ?? []).map((h) => ({
      hora: `${h.hora - 1}-${h.hora}`,
      importado: h.importado,
      exportado: -h.exportado,
    })),
    [data?.horas],
  );

  /** Abre el detalle de un día: prefiere MPP, si no el primer participante con carga. */
  const abrirDia = (fecha: string) => {
    const delDia = cargas.filter((c) => c.fecha === fecha
      && (partSel.length === 0 || partSel.includes(c.participante)));
    const elegido = delDia.find((c) => c.es_mpp) ?? delDia[0];
    if (elegido) setDiaSel({ fecha, participante: elegido.participante });
  };
  const participantesDelDia = diaSel
    ? cargas.filter((c) => c.fecha === diaSel.fecha)
    : [];

  const totales = data?.totales;
  const sinDatos = !q.isLoading && (data?.dias.length ?? 0) === 0;

  return (
    <div className="space-y-4">
      {/* ---------------------------------------------------------- encabezado */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-slate-500" /> Importaciones y Exportaciones
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Energía asignada en los enlaces internacionales. Sube los Excel de CENACE{" "}
            <b>“Energía asignada DDMMYYYY &lt;participante&gt;”</b> — puedes arrastrar{" "}
            <b>varios de una vez</b>. Cada archivo es un día de un participante y{" "}
            <b>reemplaza</b> la carga anterior de ese mismo día.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            multiple
            className="hidden"
            onChange={(e) => onArchivos(e.target.files)}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={subir.isPending}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" /> {subir.isPending ? "Cargando…" : "Cargar archivos"}
          </button>
          <button
            type="button"
            title="Descarga TODO el rango y los filtros de arriba en un solo Excel"
            onClick={() =>
              descargarIntercambiosXlsx(desde || null, hasta || null, partSel, puntosSel)
                .catch((e) => setError(e instanceof Error ? e.message : "No se pudo exportar"))}
            disabled={sinDatos}
            className="inline-flex items-center gap-2 border text-slate-600 rounded px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Excel del rango
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}

      {/* ------------------------------------------------- resultado de la carga */}
      {resultados && (
        <div className="bg-white border rounded-lg p-3 text-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-700">Resultado de la carga</span>
            <button type="button" onClick={() => setResultados(null)}
                    className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          {resultados.map((r, i) => (
            <div key={`${r.archivo}-${i}`} className={r.ok ? "text-emerald-700" : "text-red-700"}>
              {r.ok ? (
                <>
                  ✓ <b>{r.participante}</b> {r.fecha && fmtFecha(r.fecha)} · {r.unidades} unidades ·
                  {" "}importó {nf0.format(r.importado ?? 0)} MWh, exportó{" "}
                  {nf0.format(r.exportado ?? 0)} MWh
                  {r.avisos?.length ? (
                    <span className="text-amber-700"> · {r.avisos.join(" · ")}</span>
                  ) : null}
                </>
              ) : (
                <>✗ <b>{r.archivo}</b>: {r.error}</>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ------------------------------------------------------------- filtros */}
      <div className="bg-white border rounded-lg p-3 flex flex-wrap items-end gap-4">
        <label className="text-xs text-slate-500">
          Desde
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                 className="block border rounded px-2 py-1 text-sm text-slate-800 mt-0.5" />
        </label>
        <label className="text-xs text-slate-500">
          Hasta
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                 className="block border rounded px-2 py-1 text-sm text-slate-800 mt-0.5" />
        </label>
        <div className="text-xs text-slate-500">
          Participante
          <div className="flex flex-wrap gap-1 mt-1">
            <button
              type="button"
              onClick={() => setPartSel([])}
              className={`rounded-full px-2.5 py-1 text-xs border ${
                partSel.length === 0
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              Todos
            </button>
            {(data?.participantes ?? []).map((p) => (
              <button
                key={p.participante}
                type="button"
                title={p.nombre ?? p.participante}
                onClick={() => setPartSel(
                  partSel.includes(p.participante)
                    ? partSel.filter((x) => x !== p.participante)
                    : [...partSel, p.participante])}
                className={`rounded-full px-2.5 py-1 text-xs border ${
                  partSel.includes(p.participante)
                    ? "bg-slate-800 text-white border-slate-800"
                    : p.es_mpp
                      ? "bg-blue-50 text-blue-700 border-blue-300 font-medium hover:bg-blue-100"
                      : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                {p.participante}{p.nombre ? ` · ${p.nombre}` : ""}{p.es_mpp ? " (MPP)" : ""}
              </button>
            ))}
          </div>
        </div>
        <div className="text-xs text-slate-500">
          Punto de interconexión
          <div className="flex flex-wrap gap-1 mt-1">
            <button
              type="button"
              onClick={() => setPuntosSel([])}
              className={`rounded-full px-2.5 py-1 text-xs border ${
                puntosSel.length === 0
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 hover:bg-slate-50"}`}
            >
              Todos
            </button>
            {puntos.map((p, i) => (
              <button
                key={p.punto}
                type="button"
                title={`${p.punto}${p.region ? ` · gerencia ${p.region}` : ""}`}
                onClick={() => setPuntosSel(
                  puntosSel.includes(p.punto)
                    ? puntosSel.filter((x) => x !== p.punto)
                    : [...puntosSel, p.punto])}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs border ${
                  puntosSel.length === 0 || puntosSel.includes(p.punto)
                    ? "bg-white text-slate-700 border-slate-300"
                    : "bg-slate-50 text-slate-400 border-slate-200"}`}
              >
                <span className="w-2.5 h-2.5 rounded-full"
                      style={{ background: colorDe(p.punto, i) }} />
                {p.nombre}
              </button>
            ))}
          </div>
        </div>
      </div>

      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {sinDatos && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          {cargas.length === 0
            ? "Sin archivos cargados — sube los Excel de energía asignada para empezar."
            : "No hay intercambios en el rango o con los filtros elegidos."}
        </div>
      )}

      {/* ---------------------------------------------------------------- KPIs */}
      {totales && !sinDatos && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi titulo="Importado" valor={totales.importado} unidad="MWh" color="text-emerald-700" />
          <Kpi titulo="Exportado" valor={totales.exportado} unidad="MWh" color="text-rose-700" />
          <Kpi
            titulo={totales.neto >= 0 ? "Importador neto" : "Exportador neto"}
            valor={Math.abs(totales.neto)}
            unidad="MWh"
            color={totales.neto >= 0 ? "text-emerald-700" : "text-rose-700"}
          />
          <Kpi titulo="Días con datos" valor={totales.dias} unidad="" color="text-slate-800" />
        </div>
      )}

      {/* --------------------------------------------------- archivos cargados */}
      {cargas.length > 0 && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <div className="px-3 py-2 border-b text-sm font-medium text-slate-700">
            Archivos cargados
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">Participante</th>
                <th className="px-3 py-2">Archivo</th>
                <th className="px-3 py-2 text-right">Unidades</th>
                <th className="px-3 py-2 text-right">Importado</th>
                <th className="px-3 py-2 text-right">Exportado</th>
                <th className="px-3 py-2">Cargado</th>
                <th className="px-3 py-2 w-16" />
              </tr>
            </thead>
            <tbody>
              {cargas.map((c) => (
                <tr key={c.id_carga} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-1.5 tabular-nums">
                    <button type="button" className="text-blue-700 hover:underline"
                            onClick={() => setDiaSel({ fecha: c.fecha, participante: c.participante })}>
                      {fmtFecha(c.fecha)}
                    </button>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={c.es_mpp ? "font-semibold text-blue-700" : ""}>
                      {c.participante}
                    </span>
                    {c.nombre_participante ? (
                      <span className="text-slate-500"> · {c.nombre_participante}</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{c.archivo ?? "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{c.unidades}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">
                    {nf0.format(c.importado)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">
                    {nf0.format(c.exportado)}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">
                    {c.cargado_por ?? "—"} ·{" "}
                    {new Date(c.cargado_en).toLocaleString("es-MX", {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        title={`Descargar solo este día en Excel (${c.participante}, ${fmtFecha(c.fecha)})`}
                        onClick={() =>
                          descargarIntercambiosXlsx(c.fecha, c.fecha, [c.participante], [])
                            .catch((e) => setError(
                              e instanceof Error ? e.message : "No se pudo descargar"))}
                        className="text-slate-400 hover:text-blue-600"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title="Borrar esta carga (y su detalle horario)"
                        onClick={() => {
                          if (window.confirm(
                            `¿Borrar la carga de ${c.participante} del ${fmtFecha(c.fecha)}?`)) {
                            borrar.mutate(c.id_carga);
                          }
                        }}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* -------------------------------------------------- detalle de un día */}
      {diaSel && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b bg-slate-50">
            <div className="text-sm text-slate-700">
              <b>{fmtFecha(diaSel.fecha)}</b>
              {participantesDelDia.length > 1 ? (
                <select
                  value={diaSel.participante}
                  onChange={(e) => setDiaSel({ ...diaSel, participante: e.target.value })}
                  className="ml-2 border rounded px-2 py-0.5 text-sm"
                >
                  {participantesDelDia.map((c) => (
                    <option key={c.participante} value={c.participante}>
                      {c.participante}{c.nombre_participante ? ` · ${c.nombre_participante}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="ml-2 text-slate-500">
                  {diaQ.data?.participante}
                  {diaQ.data?.nombre ? ` · ${diaQ.data.nombre}` : ""}
                </span>
              )}
            </div>
            <button type="button" onClick={() => setDiaSel(null)}
                    className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          {diaQ.isLoading && <div className="p-6 text-center text-slate-500 text-sm">Cargando…</div>}
          {diaQ.isError && (
            <div className="p-6 text-center text-red-600 text-sm">{(diaQ.error as Error).message}</div>
          )}
          {diaQ.data && <TablaDia dia={diaQ.data} />}
        </div>
      )}

      {/* ------------------------------------------------- flujo diario apilado */}
      {serieDias.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-medium text-slate-800 text-sm">Flujo diario por punto</span>
            <span className="text-xs text-slate-400">
              importación arriba, exportación abajo · haz clic en un día para ver su detalle
            </span>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart
                data={serieDias}
                // Sin "sign", Recharts apila algebraicamente: la exportación
                // arrancaría en la punta de la importación y la taparía. Con
                // "sign" cada signo se apila desde el cero hacia su lado.
                stackOffset="sign"
                margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
                onClick={(e) => {
                  const f = e?.activePayload?.[0]?.payload?.fecha;
                  if (typeof f === "string") abrirDia(f);
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }}
                       tickFormatter={(v) => fmtFecha(String(v)).slice(0, 6)} />
                <YAxis tick={{ fontSize: 11 }} width={62}
                       tickFormatter={(v) => nf0.format(Math.abs(v as number))} />
                <Tooltip
                  cursor={{ fill: "rgba(15,23,42,0.04)" }}
                  labelFormatter={(l) => fmtFecha(String(l))}
                  formatter={(v: number, name: string) => {
                    const [pre, punto] = name.split("_");
                    return [
                      `${nf1.format(Math.abs(v))} MWh`,
                      `${pre === "imp" ? "Importación" : "Exportación"} · ${punto}`,
                    ];
                  }}
                />
                <ReferenceLine y={0} stroke="#94a3b8" />
                {puntosVisibles.map((p, i) => (
                  <Bar key={`imp_${p.punto}`} dataKey={`imp_${p.punto}`} stackId="flujo"
                       fill={colorDe(p.punto, i)} />
                ))}
                {puntosVisibles.map((p, i) => (
                  <Bar key={`exp_${p.punto}`} dataKey={`exp_${p.punto}`} stackId="flujo"
                       fill={colorDe(p.punto, i)} fillOpacity={0.5} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- perfil horario */}
      {serieHoras.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-baseline gap-2 mb-3">
            <span className="font-medium text-slate-800 text-sm">Perfil horario del periodo</span>
            <span className="text-xs text-slate-400">
              MWh acumulados en cada hora del día
            </span>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <BarChart data={serieHoras} stackOffset="sign"
                        margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="hora" tick={{ fontSize: 10 }} interval={1} />
                <YAxis tick={{ fontSize: 11 }} width={62}
                       tickFormatter={(v) => nf0.format(Math.abs(v as number))} />
                <Tooltip
                  cursor={{ fill: "rgba(15,23,42,0.04)" }}
                  labelFormatter={(l) => `Hora ${l}`}
                  formatter={(v: number, name: string) => [
                    `${nf1.format(Math.abs(v))} MWh`,
                    name === "importado" ? "Importación" : "Exportación",
                  ]}
                />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar dataKey="importado" stackId="h" fill="#10b981" />
                <Bar dataKey="exportado" stackId="h" fill="#f43f5e" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ titulo, valor, unidad, color }: {
  titulo: string; valor: number; unidad: string; color: string;
}) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="text-xs text-slate-500">{titulo}</div>
      <div className={`text-2xl font-semibold tabular-nums ${color}`}>
        {nf0.format(valor)}
        {unidad && <span className="text-sm font-normal text-slate-400 ml-1">{unidad}</span>}
      </div>
    </div>
  );
}

/** El pivote del día, con las mismas columnas del Excel de CENACE. */
function TablaDia({ dia }: { dia: NonNullable<Awaited<ReturnType<typeof getIntercambioDia>>> }) {
  const horas = dia.horas;
  // La intensidad del fondo se escala contra el mayor MWh horario del día, así
  // se ve de un vistazo en qué horas se concentró el flujo.
  const max = Math.max(1, ...dia.filas.flatMap((f) => f.mwh.map(Math.abs)));
  return (
    <div className="overflow-x-auto">
      <table className="text-xs">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left sticky left-0 bg-slate-100">Clave</th>
            <th className="px-3 py-2 text-left">Punto</th>
            <th className="px-2 py-2">Tipo</th>
            <th className="px-3 py-2 text-right">Total</th>
            {Array.from({ length: horas }, (_, h) => (
              <th key={h} className="px-1.5 py-2 text-right font-normal tabular-nums">
                {h}-{h + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dia.filas.map((f) => (
            <tr key={f.clave} className="border-t hover:bg-slate-50">
              <td className="px-3 py-1 font-mono sticky left-0 bg-white">{f.clave}</td>
              <td className="px-3 py-1 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full"
                        style={{ background: colorDe(f.punto, 0) }} />
                  {f.nombre_punto}
                </span>
              </td>
              <td className="px-2 py-1 text-center">
                <span className={`inline-block text-[10px] font-medium rounded-full px-1.5 py-0.5 ${
                  f.tipo === "IMP" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                  {f.tipo}
                </span>
              </td>
              <td className="px-3 py-1 text-right tabular-nums font-semibold">
                {nf0.format(f.total)}
              </td>
              {f.mwh.map((v, h) => (
                <td
                  key={h}
                  className="px-1.5 py-1 text-right tabular-nums"
                  style={v ? {
                    background: f.tipo === "IMP"
                      ? `rgba(16,185,129,${0.08 + 0.5 * (Math.abs(v) / max)})`
                      : `rgba(244,63,94,${0.08 + 0.5 * (Math.abs(v) / max)})`,
                  } : undefined}
                >
                  {v ? nf0.format(v) : <span className="text-slate-300">·</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 border-t text-xs text-slate-500">
        {dia.filas.length} unidades · archivo {dia.archivo ?? "—"} · cargado por{" "}
        {dia.cargado_por ?? "—"}
      </div>
    </div>
  );
}
